import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Form validation error component
const FormError = ({ field, errors }) => {
  if (!errors || typeof errors !== 'object') return null;
  const error = errors[field];
  return error ? <div style={{ color: '#d32f2f', fontSize: '0.8rem', marginTop: '0.25rem' }}>{error}</div> : null;
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Section = styled.section`
  padding: 6rem 5%;
  background: #f8faf9;
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Content = styled.div`
  animation: ${fadeIn} 0.6s ease-out forwards;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #0A3B2D;
  line-height: 1.2;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #4a6b63;
  margin-bottom: 2rem;
  line-height: 1.7;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2.5rem 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.2rem;
  color: #2a5e4f;
  font-weight: 500;
  
  &::before {
    content: '✓';
    display: inline-block;
    width: 24px;
    height: 24px;
    background: #0A3B2D;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    flex-shrink: 0;
    font-size: 0.8rem;
  }
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 30px rgba(10, 59, 45, 0.08);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out 0.2s forwards;
  opacity: 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background: linear-gradient(90deg, #4CAF50, #8BC34A);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: #2a5e4f;
`;

const Input = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid #e0e9e6;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #f8faf9;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.8rem 1rem;
  border: 1px solid #e0e9e6;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  background: #f8faf9;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
  }
`;

const SubmitButton = styled.button`
  background: #0A3B2D;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
  
  &:hover {
    background: #0d4f3c;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(10, 59, 45, 0.2);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SuccessMessage = styled.div`
  background: #e8f5e9;
  color: #2e7d32;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
`;

const Decoration = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(200, 230, 201, 0.3), rgba(232, 245, 233, 0.1));
  z-index: 1;
  
  &:nth-child(1) {
    top: -150px;
    right: -100px;
    width: 400px;
    height: 400px;
  }
  
  &:nth-child(2) {
    bottom: -150px;
    left: -100px;
    width: 350px;
    height: 350px;
  }
`;

const PartnerSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Web3Forms submission handler - Updated for production deployment
  const submitToWeb3Forms = async (formData) => {
    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        access_key: process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || 'YOUR_ACCESS_KEY',
        name: formData.name,
        email: formData.email,
        company: formData.company || 'Not specified',
        message: formData.message,
        subject: `New Contact Form Submission from ${formData.name}`,
        from_name: 'Surevest Contact Form',
        to_email: 'info@3bs.co.za'
      })
    });

    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      // Submit to Web3Forms
      const result = await submitToWeb3Forms(formData);

      if (result.success) {
        // Show success message
        setShowSuccess(true);

        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          message: ''
        });

        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      } else {
        throw new Error(result.message || 'Submission failed');
      }

    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitError('Failed to send message. Please try again later or contact us directly at info@3bs.co.za');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section id="partner">
      <Decoration />
      <Decoration />
      <Container>
        <Content>
          <SectionTitle>Partner With Us</SectionTitle>
          <SectionSubtitle>
            Join forces with Surevest and unlock new opportunities for growth and success. 
            Let's build something remarkable together.
          </SectionSubtitle>
          
          <FeatureList>
            <FeatureItem>Access to innovative financial solutions</FeatureItem>
            <FeatureItem>Dedicated support and expertise</FeatureItem>
            <FeatureItem>Customized partnership programs</FeatureItem>
            <FeatureItem>Exclusive resources and tools</FeatureItem>
          </FeatureList>
        </Content>
        
        <FormContainer>
          {showSuccess && (
            <SuccessMessage>
              <span>✓</span> Thank you! Your message has been sent. We'll be in touch soon.
            </SuccessMessage>
          )}
          
          {submitError && (
            <div style={{ color: '#d32f2f', marginBottom: '1rem', padding: '1rem', background: '#ffebee', borderRadius: '8px' }}>
              {submitError}
            </div>
          )}
          
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="name">Full Name *</Label>
              <Input 
                type="text" 
                id="name" 
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
              />
              <FormError field="name" errors={errors} />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="email">Business Email *</Label>
              <Input 
                type="email" 
                id="email" 
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
              />
              <FormError field="email" errors={errors} />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="company">Company Name</Label>
              <Input 
                type="text" 
                id="company" 
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your company (optional)"
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="message">How can we help? *</Label>
              <TextArea 
                id="message" 
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your partnership interests..."
              />
              <FormError field="message" errors={errors} />
            </FormGroup>
            
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
              {!isSubmitting && <span>→</span>}
            </SubmitButton>
          </Form>
        </FormContainer>
      </Container>
    </Section>
  );
};

export default PartnerSection;
